import React, { useState, useRef, useEffect } from 'react';
import axios from 'axios';
import './Chatbot.css';
import { Link } from "react-router-dom";

const Chatbot = () => {
    const [isOpen, setIsOpen] = useState(false);
    const [messages, setMessages] = useState([
        {
            id: 1,
            text: "🌟 **Welcome to PrimeDale Tours!**\n\nI'm your AI travel assistant.\n\n**I can help you with:**\n• 📦 Tour packages & destinations\n• 🛎️ Services (Corporate, MICE, Team Building, Agri-Tourism)\n• ❓ FAQs & travel tips\n• 📅 Booking & payments\n• 📰 Latest news & blogs\n• 📞 Contact information\n\n**What would you like to know?**",
            sender: 'bot',
            timestamp: new Date(),
            learnedId: null,
            showFeedback: false,
            feedbackGiven: false,
            feedbackMessage: null
        }
    ]);
    const [inputValue, setInputValue] = useState('');
    const [isTyping, setIsTyping] = useState(false);
    const [suggestedPackages, setSuggestedPackages] = useState([]);
    const [suggestedServices, setSuggestedServices] = useState([]);
    const [showSuggestions, setShowSuggestions] = useState(true); // State to control visibility of suggestions
    const messagesEndRef = useRef(null);
    const inputRef = useRef(null);
    const messagesContainerRef = useRef(null); // Ref for the messages container
    const suggestionsRef = useRef(null); // Ref for the suggestions section

    const API_URL = process.env.REACT_APP_API_URL;

    useEffect(() => {
        fetchData();
    }, []);

    const fetchData = async () => {
        try {
            const [packagesRes, servicesRes] = await Promise.all([
                axios.get(`${API_URL}/packages`),
                axios.get(`${API_URL}/primedale_services`)
            ]);

            // Handle paginated packages response
            if (packagesRes.data && packagesRes.data.data) {
                setSuggestedPackages(packagesRes.data.data.slice(0, 6));
            } else if (packagesRes.data && Array.isArray(packagesRes.data)) {
                setSuggestedPackages(packagesRes.data.slice(0, 6));
            }

            // Handle services response
            if (servicesRes.data && Array.isArray(servicesRes.data)) {
                setSuggestedServices(servicesRes.data.slice(0, 4));
            }
        } catch (error) {
            console.error('Error fetching data:', error);
        }
    };

    const scrollToBottom = () => {
        messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
    };

    useEffect(() => {
        scrollToBottom();
    }, [messages, isTyping]);

    // Handle scroll to hide/show suggestions
    const handleScroll = () => {
        if (!messagesContainerRef.current || !suggestionsRef.current) return;

        const messagesContainer = messagesContainerRef.current;
        const suggestionsHeight = suggestionsRef.current.offsetHeight;
        const scrollTop = messagesContainer.scrollTop;

        // Hide suggestions when scrolled down more than the suggestions height
        if (scrollTop > suggestionsHeight / 2) {
            setShowSuggestions(false);
        } else {
            setShowSuggestions(true);
        }
    };

    const handleLinkClick = () => {
        setIsOpen(false);
    };
    // Send feedback to backend
    const sendFeedback = async (learnedId, helpful, messageId) => {
        if (!learnedId) {
            console.warn('No learnedId provided for feedback');
            // Still show feedback confirmation even without learnedId
            setMessages(prevMessages =>
                prevMessages.map(msg =>
                    msg.id === messageId
                        ? {
                            ...msg,
                            showFeedback: false,
                            feedbackGiven: true,
                            feedbackMessage: '✅ Thanks for your feedback!'
                        }
                        : msg
                )
            );
            return;
        }

        try {
            // Update UI immediately to show feedback received
            setMessages(prevMessages =>
                prevMessages.map(msg =>
                    msg.id === messageId
                        ? {
                            ...msg,
                            showFeedback: false,
                            feedbackGiven: true,
                            feedbackMessage: helpful ? '✅ Thanks for the positive feedback!' : '📝 Thanks for letting us know. We\'ll improve this!'
                        }
                        : msg
                )
            );

            // Send to backend
            await axios.post(`${API_URL}/api/chatbot/feedback`, {
                learnedId: learnedId,
                helpful: helpful,
                message: messages.find(m => m.id === messageId)?.text || ''
            });

            console.log('Feedback sent:', { learnedId, helpful });
        } catch (error) {
            console.error('Error sending feedback:', error);
            // Revert UI if error occurs
            setMessages(prevMessages =>
                prevMessages.map(msg =>
                    msg.id === messageId
                        ? { ...msg, showFeedback: true, feedbackGiven: false, feedbackMessage: null }
                        : msg
                )
            );
            alert('Failed to send feedback. Please try again.');
        }
    };

    const sendToChatbotAPI = async (userMessage) => {
        try {
            const response = await axios.post(`${API_URL}/api/chatbot`, { message: userMessage });

            // Ensure we always have showFeedback and learnedId properties
            let botResponse = response.data.response || "I'm not sure how to respond to that. Please contact our support team for assistance.";
            let learnedId = response.data.learnedId || null;
            // Show feedback for ALL bot responses except errors/welcome messages
            let showFeedback = response.data.showFeedback !== undefined ? response.data.showFeedback : true;
            let link = response.data.link;
            // Don't show feedback for very short or error-like responses
            if (botResponse.includes("trouble connecting") || botResponse.length < 20) {
                showFeedback = false;
            }

            return { text: botResponse, learnedId, showFeedback, link };
        } catch (error) {
            console.error('API Error:', error);
            return {
                text: "I'm having trouble connecting. Please call us at +254 745 715 136 for immediate assistance!",
                learnedId: null,
                showFeedback: false
            };
        }
    };

    const handleSendMessage = async () => {
        if (!inputValue.trim()) return;

        const userMessage = {
            id: Date.now(),
            text: inputValue,
            sender: 'user',
            timestamp: new Date(),
            learnedId: null,
            showFeedback: false,
            feedbackGiven: false,
            feedbackMessage: null
        };
        setMessages(prev => [...prev, userMessage]);
        setInputValue('');
        setIsTyping(true);

        const { text: botReply, learnedId, showFeedback, link } = await sendToChatbotAPI(userMessage.text);

        const botMessage = {
            id: Date.now() + 1,
            text: botReply,
            sender: 'bot',
            link: link,
            timestamp: new Date(),
            learnedId: learnedId,
            showFeedback: showFeedback,
            feedbackGiven: false,
            feedbackMessage: null
        };
        setMessages(prev => [...prev, botMessage]);
        setIsTyping(false);
    };

    const handleQuickAction = (action) => {
        setInputValue(action);
        setTimeout(() => handleSendMessage(), 100);
    };

    const handleKeyPress = (e) => {
        if (e.key === 'Enter' && !e.shiftKey) {
            e.preventDefault();
            handleSendMessage();
        }
    };

    const formatTime = (date) => {
        return date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
    };

    const formatMessageText = (text) => {
        if (!text) return null;
        return text.split('\n').map((line, i) => {
            if (line.startsWith('**') && line.endsWith('**')) {
                return <h4 key={i} className="message-header">{line.replace(/\*\*/g, '')}</h4>;
            }
            if (line.startsWith('•')) {
                return <div key={i} className="bullet-point">{line}</div>;
            }
            if (line.match(/^\d+️⃣/)) {
                return <div key={i} className="numbered-point">{line}</div>;
            }
            return <div key={i} className="text-line">{line}</div>;
        });
    };

    return (
        <>
            {!isOpen && (
                <button className="pd-chat-toggle" onClick={() => setIsOpen(true)}>
                    <span className="toggle-image-container">
                        <img
                            src={'/assets/img/chatbot.png'}
                            alt="Travel Assistant Avatar"
                            className="pd-avatar"
                            loading="lazy"

                        />
                    </span>
                    <span className="toggle-text">Travel Assistant</span>
                    <span className="online-dot"></span>
                </button>
            )}

            {isOpen && (
                <div className="pd-chat-container">
                    <div className="pd-header">
                        <div className="pd-header-info">
                            <div className="pd-avatar">🗺️</div>
                            <div>
                                <h3>PrimeDale Tours</h3>
                                <p>Online • Live Database • 24/7 Support</p>
                            </div>
                        </div>
                        <button className="pd-close-btn" onClick={() => setIsOpen(false)}>✕</button>
                    </div>

                    {/* Quick Action Buttons - Always visible */}
                    <div className="pd-quick-actions">
                        <button onClick={() => handleQuickAction("Show me all packages")}> Packages</button>
                        <button onClick={() => handleQuickAction("What services do you offer?")}> Services</button>
                        <button onClick={() => handleQuickAction("How to book?")}> Booking</button>
                        <button onClick={() => handleQuickAction("Contact information")}> Contact</button>
                    </div>

                    {/* Popular Packages & Our Services - Hide on scroll */}
                    <div
                        ref={suggestionsRef}
                        className={`pd-suggestions-wrapper ${!showSuggestions ? 'pd-suggestions-hidden' : ''}`}
                        style={{
                            transition: 'all 0.3s ease-in-out',
                            maxHeight: showSuggestions ? '500px' : '0',
                            opacity: showSuggestions ? 1 : 0,
                            overflow: 'hidden'
                        }}
                    >
                        {/* Popular Packages */}
                        {/* {suggestedPackages.length > 0 && (
                            <div className="pd-suggested-section">
                                <p>🔥 Popular Packages</p>
                                <div className="pd-suggested-chips">
                                    {suggestedPackages.map(pkg => (
                                        <button
                                            key={pkg.id}
                                            onClick={() => handleQuickAction(`Tell me about ${pkg.title}`)}
                                            className="pd-chip"
                                        >
                                            {pkg.destination}
                                        </button>
                                    ))}
                                </div>
                            </div>
                        )}

                        {/* Our Services */}
                        {/* {suggestedServices.length > 0 && (
                            <div className="pd-suggested-section">
                                <p>⭐ Our Services</p>
                                <div className="pd-suggested-chips">
                                    {suggestedServices.map(service => (
                                        <button
                                            key={service.id}
                                            onClick={() => handleQuickAction(`Tell me about ${service.title}`)}
                                            className="pd-chip service-chip"
                                        >
                                            {service.title?.split(' ')[0] || service.title}
                                        </button>
                                    ))}
                                </div>
                            </div>
                        )}*/}
                    </div>

                    {/* Messages Container with scroll listener */}
                    <div
                        className="pd-messages"
                        ref={messagesContainerRef}
                        onScroll={handleScroll}
                        style={{ overflowY: 'auto', flex: 1 }}
                    >
                        {messages.map((msg) => (
                            <div key={msg.id} className={`pd-msg ${msg.sender}`}>
                                <div className="pd-bubble">
                                    <div className="pd-text">
                                        {formatMessageText(msg.text)}
                                    </div>

                                    {/* Feedback Row */}
                                    {msg.sender === 'bot' && msg.showFeedback && !msg.feedbackGiven && (
                                        <div className="feedback-row">
                                            <span className="feedback-label">Was this helpful?</span>
                                            <button
                                                onClick={() => sendFeedback(msg.learnedId, true, msg.id)}
                                                className="feedback-btn feedback-positive"
                                                title="Helpful"
                                                aria-label="Helpful"
                                            >
                                                👍
                                            </button>
                                            <button
                                                onClick={() => sendFeedback(msg.learnedId, false, msg.id)}
                                                className="feedback-btn feedback-negative"
                                                title="Not helpful"
                                                aria-label="Not helpful"
                                            >
                                                👎
                                            </button>
                                            {msg.link && (
                                                <Link onClick={handleLinkClick} to={msg.link} className="btn btn-outline-secondary">
                                                    ← View Package
                                                </Link>
                                            )}
                                        </div>
                                    )}

                                    {/* Feedback confirmation message */}
                                    {msg.sender === 'bot' && msg.feedbackGiven && msg.feedbackMessage && (
                                        <div className="feedback-confirmation">
                                            {msg.feedbackMessage}
                                        </div>
                                    )}

                                    <div className="pd-time">{formatTime(msg.timestamp)}</div>
                                </div>
                            </div>
                        ))}

                        {isTyping && (
                            <div className="pd-msg bot">
                                <div className="pd-typing">
                                    <span></span><span></span><span></span>
                                </div>
                            </div>
                        )}
                        <div ref={messagesEndRef} />
                    </div>

                    <div className="pd-input-wrapper">
                        <textarea
                            ref={inputRef}
                            className="pd-input"
                            placeholder="Ask me about packages, services, booking, FAQs..."
                            value={inputValue}
                            onChange={(e) => setInputValue(e.target.value)}
                            onKeyPress={handleKeyPress}
                            rows={1}
                        />
                        <button
                            className="pd-send-btn"
                            onClick={handleSendMessage}
                            disabled={!inputValue.trim()}
                        >
                            Send
                        </button>
                    </div>

                    <div className="pd-footer">
                        <span> Secure</span>
                        <span> +254 745 715 136</span>
                        <span> Live Data</span>
                        <span> 24/7</span>
                    </div>
                </div>
            )}
        </>
    );
};

export default Chatbot;